// Obra › Clima › Ahora: cómo está el tiempo en la obra en este momento, con la última foto de la cámara.
import React, { useEffect, useMemo, useState } from 'react';
import { Wind, Droplets, Gauge, Sun, Eye, Sunrise, Sunset, CloudRain, Camera, AlertTriangle } from 'lucide-react';
import R from './reglasClima';
import { Cielo, Icono3D, Semaforo, Brujula, ESTADOS, mm, grados, nivelLluvia } from './ui';
import { ultimaFoto, fotosReferencia } from './fotosObra';
import { senalCamara } from './camaraLluvia';
import { photoUrl } from '../../IADashboard/iaData';

const UV = (v) => (v == null ? null : v < 3 ? ['Bajo', '#22c55e'] : v < 6 ? ['Moderado', '#eab308'] : v < 8 ? ['Alto', '#f97316'] : v < 11 ? ['Muy alto', '#ef4444'] : ['Extremo', '#a855f7']);
const hhmm = (iso) => (iso ? iso.slice(11, 16) : '—');

function Metrica({ icon, titulo, children, pie }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 flex flex-col gap-2 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">{icon}{titulo}</p>
      <div className="flex items-center gap-3 min-w-0">{children}</div>
      {pie && <p className="text-[11px] text-gray-500 leading-snug">{pie}</p>}
    </div>
  );
}

export default function Ahora({ ix, loc, user, U, hoy, interno, onIr }) {
  const a = ix.actual || {};
  const ahoraLocal = (a.time || R.localDe(Date.now(), ix.offset)).slice(0, 16);
  const noche = a.is_day === 0;
  const kind = R.tipoTiempo(a.weather_code, a.precipitation || 0, noche) || 'nublado';
  const dHoy = ix.diasMap.get(hoy);
  const evHoy = useMemo(() => R.evaluarDia(ix, hoy, U), [ix, hoy, U]);

  // lluvia de la última hora (cuartos de hora medidos/estimados por Open-Meteo)
  const m15 = (ix.m15 || []).filter((x) => x.t <= ahoraLocal);
  const hace1h = new Date(Date.parse(ahoraLocal + ':00Z') - 60 * 60e3).toISOString().slice(0, 16);
  const ultimaHora = m15.filter((x) => x.t > hace1h).reduce((s, x) => s + x.mm, 0);
  const prox2h = (ix.m15 || []).filter((x) => x.t > ahoraLocal).reduce((s, x) => s + x.mm, 0);
  const lloviendo = (a.precipitation || 0) >= 0.1 || ultimaHora >= 0.2;

  // próximas 12 h + aviso si viene algo fuerte
  const tAhora = ahoraLocal.slice(0, 13) + ':00';
  const prox = ix.horas.filter((x) => x.t >= tAhora).slice(0, 13);
  const urgente = useMemo(() => {
    const C = { tormenta: { activo: true }, lluvia_fuerte: { activo: true, horas: 6, mm: 4 }, viento: { activo: true, kmh: U.rafagaNoApto } };
    return R.avisosClima(ix, C, U, Date.now(), { ignorarFranja: true });
  }, [ix, U]);

  const [foto, setFoto] = useState(undefined);
  useEffect(() => {
    let vivo = true;
    setFoto(undefined);
    ultimaFoto(user).then((f) => vivo && setFoto(f)).catch(() => vivo && setFoto(null));
    return () => { vivo = false; };
  }, [user?.feedID, user?.identifier]); // eslint-disable-line react-hooks/exhaustive-deps

  const [senal, setSenal] = useState(null);
  const revisarLente = async () => {
    if (!foto?.photo) return;
    setSenal({ cargando: true });
    const hora = new Date(foto.ms - 3 * 3600e3).getUTCHours();
    const refs = await fotosReferencia(user, hoy, hora);
    const urls = await Promise.all(refs.map(photoUrl));
    setSenal((await senalCamara(foto.url, urls)) || { error: 'No se pudo leer la foto (en producción S3 no permite leerla desde el navegador).' });
  };

  const uv = UV(a.uv_index);
  const fotoHora = foto?.ms ? new Date(foto.ms - 3 * 3600e3).toISOString() : null;
  const fotoVieja = foto?.ms && Date.now() - foto.ms > 3 * 3600e3;
  const pres = a.pressure_msl;
  const presAntes = ix.horas.find((x) => x.t === new Date(Date.parse(tAhora + ':00Z') - 3 * 3600e3).toISOString().slice(0, 13) + ':00');
  const tend = presAntes && presAntes.pres != null && pres != null ? pres - presAntes.pres : null;

  return (
    <div className="space-y-4 clima-in">
      {urgente.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            {urgente.map((x) => <p key={x.clave} className="text-sm text-rose-900"><b>{x.titulo.replace(/^\S+\s/, '')}:</b> {x.texto}</p>)}
          </div>
          <button type="button" onClick={() => onIr('alertas')} className="shrink-0 text-xs font-semibold text-rose-700 hover:underline">Avisame</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-4">
        {/* Condición actual con el cielo animado */}
        <div className="relative rounded-3xl overflow-hidden border border-gray-200 min-h-[260px] bg-sky-50">
          <Cielo kind={kind} level={nivelLluvia(a.precipitation || 0, true)} night={noche} alto={420} />
          <div className="relative p-5 sm:p-6 h-full flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider ${noche || kind === 'tormenta' ? 'text-white/80' : 'text-slate-600'}`}>Ahora en la obra · {hhmm(a.time)}</p>
                <div className="flex items-end gap-2 mt-1">
                  <span className={`text-6xl sm:text-7xl font-bold leading-none tabular-nums ${noche || kind === 'tormenta' ? 'text-white' : 'text-slate-900'}`}>{grados(a.temperature_2m)}</span>
                  <span className={`text-sm pb-2 ${noche || kind === 'tormenta' ? 'text-white/85' : 'text-slate-700'}`}>Sensación {grados(a.apparent_temperature)}</span>
                </div>
                <p className={`text-lg font-semibold mt-1 ${noche || kind === 'tormenta' ? 'text-white' : 'text-slate-800'}`}>{R.TIEMPO_TXT[kind]}{lloviendo ? ' · está lloviendo' : ''}</p>
                {dHoy && <p className={`text-sm ${noche || kind === 'tormenta' ? 'text-white/85' : 'text-slate-600'}`}>Hoy: máx {grados(dHoy.tmax)} · mín {grados(dHoy.tmin)} · lluvia {mm(dHoy.mm)} mm</p>}
              </div>
              <Icono3D kind={kind} size={104} className="clima-float shrink-0 -mt-1" />
            </div>
            <div className="mt-auto pt-4">
              <div className="rounded-2xl bg-white/85 backdrop-blur px-3.5 py-2.5 flex items-center gap-3 shadow-sm">
                <span className="text-2xl" aria-hidden="true">{evHoy.laborable ? ESTADOS[evHoy.estado]?.emoji || '•' : '📅'}</span>
                <div className="min-w-0 flex-1">
                  {evHoy.laborable ? (
                    <>
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">Hoy para trabajar <Semaforo estado={evHoy.estado} small /></p>
                      <p className="text-xs text-gray-600 line-clamp-2">{evHoy.resumen}{evHoy.motivos.length > 1 ? ` · ${evHoy.motivos.length - 1} más` : ''}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-gray-900">Hoy {R.fechaLarga(hoy).split(' ')[0]} no se trabaja</p>
                      <p className="text-xs text-gray-600 line-clamp-2">Si hubiera que trabajar: {evHoy.estado === 'apto' ? 'el clima acompaña' : evHoy.resumen.charAt(0).toLowerCase() + evHoy.resumen.slice(1)}</p>
                    </>
                  )}
                </div>
                <button type="button" onClick={() => onIr('pronostico')} className="text-xs font-semibold text-blue-700 hover:underline shrink-0">Ver semana</button>
              </div>
            </div>
          </div>
        </div>

        {/* Así está tu obra ahora */}
        <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-gray-900 min-h-[260px]">
          {foto === undefined && <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-700 to-gray-800" />}
          {foto && <img src={foto.url} alt="Última foto de la obra" className="absolute inset-0 w-full h-full object-cover" />}
          {foto === null && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300 gap-2 p-6 text-center">
              <Camera className="w-8 h-8" />
              <p className="text-sm">Todavía no hay una foto reciente de la cámara.</p>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/75 via-black/30 to-transparent">
            <p className="text-white text-sm font-semibold flex items-center gap-1.5"><Camera className="w-4 h-4" />Así está tu obra {fotoVieja ? 'en la última foto' : 'ahora'}</p>
            {fotoHora && <p className="text-white/80 text-xs">{fotoHora.slice(0, 10) === hoy ? 'Hoy' : fotoHora.slice(8, 10) + '/' + fotoHora.slice(5, 7)} a las {fotoHora.slice(11, 16)}</p>}
          </div>
          {interno && foto?.photo && (
            <div className="absolute top-2 right-2 left-2 flex justify-end">
              {!senal && <button type="button" onClick={revisarLente} className="text-[11px] font-semibold bg-white/90 rounded-full px-2.5 py-1 shadow">🧪 ¿La cámara muestra lluvia?</button>}
              {senal && (
                <div className="text-[11px] bg-white/95 rounded-xl px-2.5 py-1.5 shadow max-w-[90%]">
                  {senal.cargando ? 'Comparando con fotos de otros días…' : senal.error ? senal.error : <><b>{senal.probable ? 'Posible lluvia o lente mojada' : 'Se ve normal'}</b> · {senal.texto} (nitidez {Math.round(senal.actual.nitidez)}, {senal.referencias} fotos de referencia). Experimental.</>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metrica icon={<Wind className="w-3.5 h-3.5" />} titulo="Viento" pie={a.wind_gusts_10m != null && a.wind_gusts_10m >= U.gruaRafaga ? `Ráfagas por encima de ${U.gruaRafaga} km/h: no izar con grúa` : `Viene del ${R.direccionTxt(a.wind_direction_10m) || '—'}`}>
          <Brujula deg={a.wind_direction_10m} />
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 tabular-nums leading-none">{Math.round(a.wind_speed_10m ?? 0)}<span className="text-sm font-semibold text-gray-500"> km/h</span></p>
            <p className={`text-xs font-semibold mt-1 ${a.wind_gusts_10m >= U.rafagaCuidado ? 'text-amber-700' : 'text-gray-600'}`}>Ráfagas {Math.round(a.wind_gusts_10m ?? 0)} km/h</p>
          </div>
        </Metrica>
        <Metrica icon={<CloudRain className="w-3.5 h-3.5" />} titulo="Lluvia" pie={prox2h >= 0.1 ? `Próximas 2 h: ${mm(prox2h)} mm` : 'Sin lluvia en las próximas 2 h'}>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 tabular-nums leading-none">{mm(ultimaHora)}<span className="text-sm font-semibold text-gray-500"> mm</span></p>
            <p className="text-xs text-gray-600 mt-1">en la última hora</p>
          </div>
          <div className="hidden sm:flex items-end gap-0.5 h-10 ml-auto" aria-hidden="true">
            {(ix.m15 || []).slice(-16).map((x) => (
              <span key={x.t} title={`${x.t.slice(11)} · ${mm(x.mm)} mm`} className={`w-1.5 rounded-sm ${x.t > ahoraLocal ? 'bg-sky-200' : 'bg-sky-500'}`} style={{ height: `${Math.max(6, Math.min(100, x.mm * 60))}%` }} />
            ))}
          </div>
        </Metrica>
        <Metrica icon={<Droplets className="w-3.5 h-3.5" />} titulo="Humedad" pie={a.relative_humidity_2m >= 85 ? 'Muy húmedo: secado lento de pinturas y morteros' : a.relative_humidity_2m <= 30 ? 'Aire seco: curar bien el hormigón' : 'Normal para trabajar'}>
          <div className="relative w-12 h-12 shrink-0">
            <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90"><circle cx="18" cy="18" r="15" fill="none" stroke="#e0f2fe" strokeWidth="5" /><circle cx="18" cy="18" r="15" fill="none" stroke="#0ea5e9" strokeWidth="5" strokeDasharray={`${(94.2 * (a.relative_humidity_2m || 0)) / 100} 94.2`} strokeLinecap="round" /></svg>
          </div>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{a.relative_humidity_2m ?? '—'}<span className="text-sm text-gray-500"> %</span></p>
        </Metrica>
        <Metrica icon={<Gauge className="w-3.5 h-3.5" />} titulo="Presión" pie={tend == null ? 'Al nivel del mar' : tend <= -2 ? 'Bajando rápido: puede desmejorar' : tend >= 2 ? 'Subiendo: mejora el tiempo' : 'Estable'}>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{pres != null ? Math.round(pres) : '—'}<span className="text-sm text-gray-500"> hPa</span></p>
          {tend != null && <span className={`ml-auto text-xs font-semibold ${tend < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{tend > 0 ? '▲' : tend < 0 ? '▼' : '▬'} {Math.abs(tend).toFixed(1).replace('.', ',')}</span>}
        </Metrica>
        <Metrica icon={<Sun className="w-3.5 h-3.5" />} titulo="Índice UV" pie={uv ? (a.uv_index >= 6 ? 'Protector, gorra y ropa de manga larga' : 'Sin riesgo especial') : ''}>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{a.uv_index != null ? a.uv_index.toFixed(1).replace('.', ',') : '—'}</p>
          {uv && <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: uv[1] }}>{uv[0]}</span>}
        </Metrica>
        <Metrica icon={<Eye className="w-3.5 h-3.5" />} titulo="Visibilidad" pie={a.visibility != null && a.visibility < 1000 ? 'Niebla: cuidado con maquinaria y grúa' : 'Buena para operar maquinaria'}>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{a.visibility != null ? (a.visibility >= 10000 ? '10+' : (a.visibility / 1000).toFixed(1).replace('.', ',')) : '—'}<span className="text-sm text-gray-500"> km</span></p>
        </Metrica>
        <Metrica icon={<Sunrise className="w-3.5 h-3.5" />} titulo="Sol" pie={dHoy?.amanecer ? `${Math.round((Date.parse(dHoy.atardecer + ':00Z') - Date.parse(dHoy.amanecer + ':00Z')) / 3600e3 * 10) / 10} h de luz`.replace('.', ',') : ''}>
          <div className="text-sm text-gray-800 space-y-0.5">
            <p className="flex items-center gap-1.5"><Sunrise className="w-4 h-4 text-amber-500" />{hhmm(dHoy?.amanecer)}</p>
            <p className="flex items-center gap-1.5"><Sunset className="w-4 h-4 text-indigo-500" />{hhmm(dHoy?.atardecer)}</p>
          </div>
        </Metrica>
        <Metrica icon={<CloudRain className="w-3.5 h-3.5" />} titulo="Nubosidad" pie={`Rocío ${grados(ix.horas.find((x) => x.t === tAhora)?.rocio)}`}>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{a.cloud_cover ?? '—'}<span className="text-sm text-gray-500"> %</span></p>
        </Metrica>
      </div>

      {/* Próximas 12 horas */}
      <div className="bg-white border border-gray-200 rounded-2xl">
        <div className="flex items-center justify-between px-4 pt-3">
          <p className="text-sm font-semibold text-gray-900">Próximas 12 horas</p>
          <button type="button" onClick={() => onIr('pronostico')} className="text-xs font-semibold text-blue-700 hover:underline">48 h en detalle</button>
        </div>
        <div className="overflow-x-auto clima-hide-scroll">
          <div className="flex gap-1 p-3 min-w-max">
            {prox.map((x, i) => {
              const k = R.tipoTiempo(x.code, x.mm, x.dia === 0);
              const trabajo = x.h >= U.jornadaDesde && x.h < U.jornadaHasta;
              return (
                <div key={x.t} className={`w-[68px] rounded-xl px-1.5 py-2 flex flex-col items-center gap-1 ${i === 0 ? 'bg-blue-50 ring-1 ring-blue-200' : trabajo ? 'bg-gray-50' : 'bg-white'}`}>
                  <span className="text-[11px] font-semibold text-gray-500">{i === 0 ? 'Ahora' : `${x.h} h`}</span>
                  <Icono3D kind={k} size={30} />
                  <span className="text-sm font-bold text-gray-900">{grados(x.temp)}</span>
                  <span className={`text-[10px] font-semibold ${x.mm >= 0.1 ? 'text-sky-700' : 'text-gray-400'}`}>{x.mm >= 0.1 ? `${mm(x.mm)} mm` : `${x.prob ?? 0} %`}</span>
                  <span className={`text-[10px] ${x.rafaga >= U.gruaRafaga ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>💨 {Math.round(x.rafaga ?? 0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 px-1">Lluvia de la última hora: medición casi en tiempo real del servicio meteorológico (Open-Meteo, cada 15 min) para la ubicación de la obra{loc?.direccion ? ` (${loc.direccion})` : ''}.</p>
    </div>
  );
}
